// Note: some things such as fontSize can be only redefined using CSS, not the Theme (index.css)

import type { ThemeConfig } from "antd";
import { theme as antdTheme } from "antd";
import type { Theme } from "~/hooks/useDesignSystemToggle";

/**
 * Helper to get CSS variable value from document root.
 *
 * Ant Design treats an empty string as a valid colour and renders it as
 * `transparent`, so every read needs a fallback: this runs during SSR-less
 * bootstrap too, where the stylesheet may not have been applied yet.
 */
function getCSSVariable(varName: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return value || fallback;
}

/**
 * Generate Ant Design theme configuration based on current theme
 * This allows Modal and other Ant components to respond to theme changes
 *
 * DARK MODE STRATEGY:
 * Ant's *seed* tokens (`colorBgBase` / `colorTextBase`) are what its algorithm
 * derives the ~40 container, fill, border and text tokens from. Overriding a
 * handful of map tokens on top of the light algorithm leaves the rest of that
 * ramp light — which is why raw antd Input/Select/Table/Tooltip surfaces stayed
 * white on a dark canvas. We therefore switch algorithms with the theme and
 * seed both algorithms from our own CSS variables, then pin the specific
 * surfaces that must match a design-system token exactly.
 *
 * Only `dark` is a dark theme; `latte` and `overripe-banana` are light palettes.
 *
 * Z-INDEX STRATEGY:
 * We let Ant Design handle its own z-index hierarchy automatically.
 * Ant uses zIndexPopupBase (default: 1000) with internal offsets:
 * - Modal/Drawer: +0 (1000)
 * - Popover: +30 (1030)
 * - Select/DatePicker/Dropdown: +50 (1050)
 * - Tooltip: +70 (1070)
 *
 * For custom components that must appear above all Ant components,
 * use the customPopover/customToast/customTooltip tiers (2200+) from tokens.ts
 */
export function getAntdThemeConfig(theme: Theme): ThemeConfig {
  const isDark = theme === "dark";

  // Get theme-aware colors from CSS variables
  const primaryColor = getCSSVariable("--indigo-primary", "#181311");
  const primaryHoverColor = getCSSVariable("--indigo-primary-hover", "#000000");
  const textPrimary = getCSSVariable("--indigo-text-primary", "#181311");
  const textSecondary = getCSSVariable("--indigo-text-secondary", "#383736");
  const textTertiary = getCSSVariable("--indigo-text-tertiary", "#6e6d6c");
  const textDisabled = getCSSVariable("--indigo-text-disabled", "#a2a09f");
  const bgElevated = getCSSVariable("--indigo-gray-25", "#fafafa");
  const bgHighlight = getCSSVariable("--indigo-canvas-highlight", "#ffffff");
  const bgShade = getCSSVariable("--indigo-canvas-shade", "#f9f9f9");
  const bgRoot = getCSSVariable("--indigo-root-background", "#f9f9f9");
  const borderDefault = getCSSVariable("--indigo-border-default", "#dedede");
  const borderSubtle = getCSSVariable("--indigo-border-subtle", "#e6e5e5");
  const dangerColor = getCSSVariable("--indigo-danger", "#b4534d");
  const warningColor = getCSSVariable("--indigo-warning", "#ea8f0b");
  const successColor = getCSSVariable("--indigo-success", "#98ae53");
  const infoColor = getCSSVariable("--indigo-info", "#5ea4c3");
  const iconPrimary = getCSSVariable("--indigo-icon-primary", "#181311");
  const iconHover = getCSSVariable("--indigo-text-hover", "#000000");

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      // Seed tokens — the algorithm derives the whole neutral ramp from these.
      colorBgBase: bgHighlight,
      colorTextBase: textPrimary,

      // Surfaces
      colorBgContainer: bgHighlight, // Input, Select, Table, Card
      colorBgElevated: bgElevated, // Modal, Dropdown and other popups
      colorBgLayout: bgRoot, // Layout / page canvas
      // Tooltip's surface. Ant defaults to a near-black wash, which reads as a
      // hole punched in a dark canvas; the elevated surface keeps it legible.
      colorBgSpotlight: isDark ? bgElevated : "rgba(24, 19, 17, 0.85)",

      // Text
      colorText: textPrimary,
      colorTextSecondary: textSecondary,
      colorTextTertiary: textTertiary,
      colorTextDescription: textTertiary,
      colorTextPlaceholder: textTertiary,
      colorTextDisabled: textDisabled,

      // Borders and dividers
      colorBorder: borderDefault,
      colorBorderSecondary: borderSubtle,
      colorSplit: borderDefault,

      // Brand and status
      colorPrimary: primaryColor,
      colorPrimaryHover: primaryHoverColor,
      colorError: dangerColor,
      colorWarning: warningColor,
      colorSuccess: successColor,
      colorInfo: infoColor,

      // Icons
      colorIcon: iconPrimary,
      colorIconHover: iconHover,

      fontFamily: "Diatype",
    },
    components: {
      DatePicker: {
        borderRadiusSM: 15,
        fontSize: 14,
        activeShadow: "none",
      },
      Select: {
        borderRadiusSM: 8,
        fontSize: 14,
        controlHeight: 40,
      },
      Radio: {
        borderRadius: 8,
        controlHeight: 34,
        controlHeightSM: 34,
        controlHeightLG: 34,
      },
      Button: {
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 36,
        borderRadius: 8,
        paddingInlineSM: 16,
        borderRadiusLG: 8,
        borderRadiusSM: 8,
        fontWeight: 700,
      },
      Alert: {
        // Ant paints the close/status icons with these; a hardcoded white one
        // disappeared against the light-theme banner fills.
        colorIcon: iconPrimary,
        colorIconHover: iconHover,
      },
      Switch: {
        handleSize: 24,
        trackHeight: 29,
        innerMinMargin: 10,
        innerMaxMargin: 30,
      },
      Modal: {
        titleFontSize: 16,
        fontWeightStrong: 700,
        fontSize: 14,
        // Paint the modal surface `canvas-highlight` (white in light mode)
        // rather than the global `colorBgElevated` (gray-25), so `filled`
        // inputs (which fill with the near-identical `canvas-shade`) stay
        // visibly distinct. Legacy pages did this via a CSS override that only
        // loaded on some pages; the token makes every modal consistent.
        contentBg: bgHighlight,
        headerBg: bgHighlight,
      },
      Table: {
        headerBg: bgShade,
        rowHoverBg: bgShade,
      },
      Collapse: {
        marginSM: 4,
      },
    },
  };
}
